import { create } from 'zustand'
import type { Server, Channel, Category } from '@/types'
import { servers as mockServers, serverOrder as mockOrder, voiceChannelParticipants as mockVoiceParticipants } from '@/mocks/servers'
import type { VoiceParticipant } from '@/types'

interface ServerState {
  servers: Record<string, Server>
  serverOrder: string[]
  voiceChannelParticipants: Record<string, VoiceParticipant[]>

  addServer: (server: Server) => void
  removeServer: (serverId: string) => void
  updateServer: (serverId: string, updates: Partial<Server>) => void
  addChannel: (serverId: string, channel: Channel) => void
  removeChannel: (serverId: string, channelId: string) => void
  addCategory: (serverId: string, category: Category) => void
  reorderServers: (newOrder: string[]) => void
  reorderChannels: (serverId: string, categoryId: string, channelIds: string[]) => void
  moveChannelToCategory: (serverId: string, channelId: string, newCategoryId: string, targetChannelId: string, position: 'above' | 'below') => void
  getServer: (serverId: string) => Server | undefined
  getChannel: (channelId: string) => { server: Server; channel: Channel } | undefined
}

export const useServerStore = create<ServerState>((set, get) => ({
  servers: { ...mockServers },
  serverOrder: [...mockOrder],
  voiceChannelParticipants: { ...mockVoiceParticipants },

  addServer: (server) =>
    set((state) => ({
      servers: { ...state.servers, [server.id]: server },
      serverOrder: [...state.serverOrder, server.id],
    })),

  removeServer: (serverId) =>
    set((state) => {
      const { [serverId]: _, ...rest } = state.servers
      return {
        servers: rest,
        serverOrder: state.serverOrder.filter((id) => id !== serverId),
      }
    }),

  updateServer: (serverId, updates) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      return {
        servers: { ...state.servers, [serverId]: { ...server, ...updates } },
      }
    }),

  addChannel: (serverId, channel) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      return {
        servers: {
          ...state.servers,
          [serverId]: { ...server, channels: [...server.channels, channel] },
        },
      }
    }),

  removeChannel: (serverId, channelId) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      return {
        servers: {
          ...state.servers,
          [serverId]: {
            ...server,
            channels: server.channels.filter((c) => c.id !== channelId),
          },
        },
      }
    }),

  addCategory: (serverId, category) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      return {
        servers: {
          ...state.servers,
          [serverId]: {
            ...server,
            categories: [...server.categories, category],
          },
        },
      }
    }),

  reorderServers: (newOrder) =>
    set({ serverOrder: newOrder }),

  reorderChannels: (serverId, categoryId, channelIds) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      const categoryChannels = server.channels.filter((c) => c.categoryId === categoryId)
      const otherChannels = server.channels.filter((c) => c.categoryId !== categoryId)
      const reordered = channelIds.map((id) => categoryChannels.find((c) => c.id === id)!).filter(Boolean)
      return {
        servers: {
          ...state.servers,
          [serverId]: { ...server, channels: [...otherChannels, ...reordered] },
        },
      }
    }),

  moveChannelToCategory: (serverId, channelId, newCategoryId, targetChannelId, position) =>
    set((state) => {
      const server = state.servers[serverId]
      if (!server) return state
      const channel = server.channels.find((c) => c.id === channelId)
      if (!channel) return state
      const movedChannel = { ...channel, categoryId: newCategoryId }
      const remaining = server.channels.filter((c) => c.id !== channelId)
      const targetIndex = remaining.findIndex((c) => c.id === targetChannelId)
      const insertIndex = position === 'below' ? targetIndex + 1 : targetIndex
      remaining.splice(insertIndex, 0, movedChannel)
      return {
        servers: {
          ...state.servers,
          [serverId]: { ...server, channels: remaining },
        },
      }
    }),

  getServer: (serverId) => get().servers[serverId],

  getChannel: (channelId) => {
    for (const server of Object.values(get().servers)) {
      const channel = server.channels.find((c) => c.id === channelId)
      if (channel) return { server, channel }
    }
    return undefined
  },
}))
